import { useEffect, useState } from "react";
import { useStore } from "zustand";

import { useRobotState } from "../hooks/useRobotState";
import type { UseOperatorInputResult } from "../hooks/useOperatorInput";
import type { InputSourceInfo } from "../modules/teleop-input";
import type { OperatorProtocol, ControlMode } from "../modules/teleop-commands";
import { formatInputPreview } from "../lib/format-input-preview";
import styles from "./InputSourcePanel.module.scss";

export interface InputSourcePanelProps {
  input: UseOperatorInputResult;
  protocol: OperatorProtocol;
  accessGranted?: boolean;
}

const KIND_ICON: Record<InputSourceInfo["kind"], string> = {
  keyboard: "bi-keyboard",
  gamepad: "bi-controller",
  xr: "bi-badge-vr",
};

const WATCHDOG_LABEL: Record<string, string> = {
  ok: "OK",
  tripped: "сработал",
  idle: "—",
};

/** Error chips stay visible this long after the backend reported them. */
const ERROR_VISIBLE_MS = 10_000;
/** control-status comes at 2 Hz — older than this means the backend went silent. */
const STATUS_STALE_MS = 2_000;

export function InputSourcePanel({ input, protocol, accessGranted = true }: InputSourcePanelProps) {
  const { controller, state, xr } = input;
  const robotState = useRobotState();
  const control = useStore(robotState.controlStatus);
  const robotConfig = useStore(robotState.robotConfig);
  const observationOnly = !accessGranted || robotConfig.config?.robot?.control_enabled === false;
  const dryRun = robotConfig.config?.robot?.control_mode === "dry-run";
  const controlMode = robotConfig.config?.robot?.control_mode;
  const liveBaseTrial = controlMode === "live" || controlMode === "mux-live";
  const vrPrototype = robotConfig.config?.robot?.vr_control_enabled === true;
  const allowedSources = robotConfig.config?.input_sources;
  const visibleSources = state.sources.filter((source) => source.kind !== "xr");
  const xrAllowed = !allowedSources || allowedSources.includes("xr");

  useEffect(() => {
    const selected = state.sources.find((source) => source.id === state.activeId);
    if (selected && allowedSources && !allowedSources.includes(selected.kind)) {
      controller.select(null);
    }
  }, [allowedSources, controller, state.activeId, state.sources]);

  // Re-render once a second so the error chip expires even without new messages.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const stale = control.receivedAt === null || now - control.receivedAt > STATUS_STALE_MS;
  const status = stale ? null : control.status;
  const ownedByOther = status?.owner_sid != null && status.owner_sid !== protocol.sid;
  const backendSource = status?.active_source?.id ?? null;
  const inSync = !state.activeId || backendSource === state.activeId;
  const rearmRequired = liveBaseTrial && status?.rearm_required === true;
  const selectedMode: ControlMode = status?.requested_mode
    ?? (status?.active_source?.kind as ControlMode | undefined)
    ?? (state.activeId ? (state.sources.find((src) => src.id === state.activeId)?.kind ?? "off") : "off");
  const selectMode = (mode: ControlMode, sourceId: string | null = null) => {
    protocol.selectMode(mode);
    if (mode === "off" || mode === "xr") {
      controller.select(null);
    } else {
      // Re-selecting the same source must still send a fresh source announcement
      // after the backend releases the old owner during a mode change.
      if (state.activeId === sourceId) controller.select(null);
      controller.select(sourceId);
    }
  };
  const error =
    control.lastError && now - control.lastError.at < ERROR_VISIBLE_MS ? control.lastError : null;

  return (
    <section className={styles.panel} aria-label="Источник управления">
      <header className={styles.header}>
        <span className={styles.title}>Источник управления</span>
        <button
          type="button"
          className={styles.stopButton}
          onClick={() => {
            protocol.selectMode("off");
            controller.stopRobot();
          }}
          title="Отправить стоп и выключить управление"
        >
          <i className="bi bi-stop-fill" aria-hidden="true" /> Стоп
        </button>
      </header>

      <div className={styles.sources} role="radiogroup" aria-label="Активный источник">
        <label className={`${styles.source}${selectedMode === "off" ? ` ${styles.active}` : ""}`}>
          <input
            type="radio"
            name="input-source"
            checked={selectedMode === "off"}
            onChange={() => selectMode("off")}
          />
          <i className="bi bi-slash-circle" aria-hidden="true" />
          <span className={styles.sourceLabel}>Выключено</span>
        </label>
        {visibleSources.map((src) => (
          <label
            key={src.id}
            className={`${styles.source}${selectedMode === src.kind && state.activeId === src.id ? ` ${styles.active}` : ""}${
              src.connected && (!allowedSources || allowedSources.includes(src.kind)) ? "" : ` ${styles.disconnected}`
            }`}
            title={src.label}
          >
            <input
              type="radio"
              name="input-source"
              checked={selectedMode === src.kind && state.activeId === src.id}
              disabled={!src.connected || observationOnly || Boolean(allowedSources && !allowedSources.includes(src.kind))}
              onChange={() => selectMode(src.kind, src.id)}
            />
            <i className={`bi ${KIND_ICON[src.kind]}`} aria-hidden="true" />
            <span className={styles.sourceLabel}>{src.label}</span>
            {(!src.connected || (allowedSources && !allowedSources.includes(src.kind)))
              && <span className={styles.badge}>недоступно</span>}
          </label>
        ))}
        <label className={`${styles.source}${selectedMode === "xr" ? ` ${styles.active}` : ""}${
          xrAllowed && !observationOnly ? "" : ` ${styles.disconnected}`
        }`}>
            <input
              type="radio"
              name="input-source"
              checked={selectedMode === "xr"}
              disabled={observationOnly || !xrAllowed}
              onChange={() => selectMode("xr")}
            />
            <i className="bi bi-badge-vr" aria-hidden="true" />
            <span className={styles.sourceLabel}>VR (Meta Quest)</span>
            {!xrAllowed && <span className={styles.badge}>недоступно</span>}
        </label>
      </div>

      {!accessGranted && <p role="status">Подтвердите ключ Panther в блоке доступа выше.</p>}
      {accessGranted && observationOnly && (
        <p role="status">
          Управление закрыто: робот в режиме удержания. После проверки свободного пространства
          и аппаратного аварийного останова выполните на компьютере оператора
          <code> panther-teleop arm</code>. Сеанс без таймера;
          после завершения выполните <code>panther-teleop hold</code>.
        </p>
      )}

      {selectedMode === "keyboard" && state.activeId !== "keyboard" && !observationOnly && (
        <button type="button" className={styles.button} onClick={() => selectMode("keyboard", "keyboard")}>
          Включить клавиатуру
        </button>
      )}

      {xr.supported && !observationOnly && xrAllowed && selectedMode === "xr" && (
        <div className={styles.xrRow}>
          {xr.active ? (
            <button type="button" className={styles.button} onClick={() => void xr.exit()}>
              <i className="bi bi-box-arrow-left" aria-hidden="true" /> Выйти из Quest
            </button>
          ) : (
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                xr.enter().catch((err: unknown) =>
                  console.warn("[teleop-xr] enter passthrough failed", err)
                );
              }}
            >
              <i className="bi bi-badge-vr" aria-hidden="true" /> Управлять с Quest
            </button>
          )}
        </div>
      )}

      {xrAllowed && selectedMode === "xr" && !xr.supported && (
        <p>Запустите VR-ввод в Quest. Изображение камер остаётся на этом экране.</p>
      )}
      {selectedMode === "keyboard" && (
        <p>Клавиатура: W/S — вперёд/назад, A/D — влево/вправо, Q/E — поворот против/по часовой стрелке. Диагонали и поворот можно сочетать.</p>
      )}
      {selectedMode === "keyboard" && (
        <p>Лифт: <kbd>O</kbd> (англ.) — поднять, <kbd>L</kbd> (англ.) — опустить. Удерживайте клавишу для движения; отпустите для остановки. O и L одновременно — остановка лифта.</p>
      )}
      {vrPrototype && selectedMode === "xr" && (
        liveBaseTrial
          ? <p>Quest: B включает управление. Левый стик — движение базы; правый влево/вправо — поворот, вверх/вниз — лифт.</p>
          : <p>Quest: B включает и выключает управление. Левый стик — база, правый — лифт; grip — рука, триггер — схват.</p>
      )}

      <div className={styles.preview}>
        <span className={styles.previewLabel}>Ввод</span>
        <code className={styles.previewValue}>
          {selectedMode === "xr" && state.activeId === null
            ? "ожидание ввода Quest"
            : state.activeId ? formatInputPreview(state.preview) : "управление выключено"}
        </code>
      </div>

      <dl className={styles.status}>
        <dt>Бекенд</dt>
        <dd className={stale ? styles.bad : styles.ok}>{stale ? "не отвечает" : "на связи"}</dd>
        <dt>{observationOnly ? "ROS2 мост" : "Планировщик"}</dt>
        <dd className={status?.robot_link === "connected" ? styles.ok : styles.bad}>
          {status ? (status.robot_link === "connected" ? "подключён" : "нет связи") : "—"}
        </dd>
        <dt>Watchdog</dt>
        <dd className={status?.watchdog === "tripped" ? styles.bad : undefined}>
          {status ? WATCHDOG_LABEL[status.watchdog] : "—"}
        </dd>
        {status?.monitor && status.monitor !== "not-required" && (
          <>
            <dt>Монитор</dt>
            <dd className={status.monitor === "live" ? styles.ok : styles.bad}>
              {status.monitor === "live" ? "видео есть" : "нет видео"}
            </dd>
          </>
        )}
        <dt>Приём ввода</dt>
        <dd>{status ? `${status.input_rate_hz.toFixed(0)} Гц` : "—"}</dd>
        <dt>Управление</dt>
        <dd className={(ownedByOther && selectedMode !== "xr") || !inSync || rearmRequired ? styles.bad : undefined}>
          {observationOnly ? "наблюдение" : dryRun ? "проверка ввода (без движения)"
            : rearmRequired ? "остановлено — выберите источник снова"
            : selectedMode === "xr" ? (status?.active_source?.kind === "xr" ? "VR активен в Quest" : "ожидание Quest")
            : ownedByOther ? "занято другой сессией"
            : !inSync ? "ждёт подтверждения"
            : selectedMode === "off" ? "выключено" : "клавиатура активна"}
        </dd>
      </dl>

      {error && (
        <div className={styles.error} role="status">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" /> {error.code}
          {error.detail ? `: ${error.detail}` : ""}
        </div>
      )}
    </section>
  );
}
